'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import { Tag } from '@/components/elementary/Tag'
import PricingBenefits from '@/components/page-builder/global/pricing/PricingBenefits'
import PricingCardBody from '@/components/page-builder/global/pricing/PricingCardBody'
import PricingCardFooter from '@/components/page-builder/global/pricing/PricingCardFooter'
import PricingCardHeader from '@/components/page-builder/global/pricing/PricingCardHeader'
import PricingBanner from '@/components/shared/PricingBanner'
import Heading from '@/components/typography/Heading'
import { isRefSticky } from '@/hooks/useSticky'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export const PricingListContent = ({
	cardsData,
	pricingBanner,
	currentTab,
	yearly = false,
	className,
}: {
	cardsData?: Data.Component<'shared.pricing-item'>[] | null
	pricingBanner?: Data.Component<'shared.pricing-banner'> | null
	currentTab?: string | null
	yearly?: boolean
	className?: string
}) => {
	const colsStyle = !currentTab
		? { gridTemplateColumns: `repeat(${cardsData?.length}, minmax(0, 1fr))` }
		: undefined
	const commonGridPartsClass =
		'border-x-[0.5px] first:border-l-0 border-primary last:border-r-0 bg-peach-200'

	const t = useTranslations('pricing')

	const cardsDataFiltered = currentTab
		? cardsData?.filter((item) => item.label === currentTab)
		: cardsData

	const stickyRef = useRef<HTMLDivElement>(null)
	const isStuck = isRefSticky(stickyRef, 100)

	return (
		<div className={cn('relative mt-5', className)} data-test-id={PRICING_TEST_IDS.LIST_CONTENT}>
			<div className="grid" style={colsStyle}>
				{cardsDataFiltered?.map((item) => (
					<div
						key={`${item?.id}-top`}
						className={cn(
							'pt-5 first:rounded-tl-xl last:rounded-tr-xl ',
							commonGridPartsClass,
							item.isPopularTagVisible && 'bg-peach-300',
						)}
					/>
				))}
			</div>

			<div
				ref={stickyRef}
				className={cn('grid lg:sticky top-25 z-2 ', isStuck && 'lg:border-b lg:border-brand-black')}
				style={colsStyle}
			>
				{cardsDataFiltered?.map((item) => (
					<div
						key={`${item?.id}-titles`}
						className={cn(
							'pb-2 px-4 pt-3 w-full flex flex-wrap gap-x-2 gap-y-1 relative ',
							commonGridPartsClass,
							item.isPopularTagVisible && 'bg-peach-300',
						)}
					>
						<Heading tag="h2" className="w-fit" fontSize="headingPricingTitle">
							{item?.label}
						</Heading>
						{item?.isPopularTagVisible && (
							<Tag
								name={t('mostPopular')}
								size="medium"
								className="w-fit"
								active
								fontWeight="font-bold"
								customColors={{
									backgroundColor: 'neutral-light-100',
									borderColor: 'brand-black',
								}}
							/>
						)}
					</div>
				))}
			</div>

			<div className="flex flex-col">
				<div className="grid" style={colsStyle}>
					{cardsDataFiltered?.map((item) => (
						<PricingCardHeader
							className={cn(commonGridPartsClass, item.isPopularTagVisible && 'bg-peach-300')}
							key={`${item?.id}-header`}
							pricingItem={item}
						/>
					))}
				</div>

				<div className="grid" style={colsStyle}>
					{cardsDataFiltered?.map((item) => (
						<PricingCardBody
							className={cn(
								'lg:pt-5',
								commonGridPartsClass,
								item.isPopularTagVisible && 'bg-peach-300',
							)}
							key={`${item?.id}-body`}
							pricingItem={item}
							yearly={yearly}
						/>
					))}
				</div>
				<div className="grid" style={colsStyle}>
					{cardsDataFiltered?.map((item) => (
						<PricingCardFooter
							className={cn(
								'pt-5 first:rounded-bl-xl last:rounded-br-xl',
								commonGridPartsClass,
								item.isPopularTagVisible && 'bg-peach-300',
							)}
							key={`${item?.id}-footer`}
							pricingItem={item}
							yearly={yearly}
						/>
					))}
				</div>
			</div>

			{pricingBanner && (
				<div className="mt-5">
					<PricingBanner component={pricingBanner} />
				</div>
			)}

			{Array.isArray(cardsDataFiltered) &&
			cardsDataFiltered?.some(
				(item) => Array.isArray(item?.benefits) && item.benefits.length > 0,
			) ? (
				<div className="mt-5">
					<div className="grid" style={colsStyle}>
						{cardsDataFiltered?.map((item) => (
							<PricingBenefits
								className={cn(
									'pt-5 first:rounded-l-xl last:rounded-r-xl',
									commonGridPartsClass,
									item.isPopularTagVisible && 'lg:bg-peach-300',
								)}
								key={`${item?.id}-benefits`}
								pricingItem={item}
							/>
						))}
					</div>
				</div>
			) : null}
		</div>
	)
}

export default PricingListContent
